import React, { useContext, useMemo, useRef } from 'react';
import { useTranslationHelpBlog } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { generatePath, Link, useNavigate, useParams } from 'react-router-dom';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { HelpBlogLayout } from '@dealstack/frontend/app/pages/HelpBlog/HelpBlogLayout/HelpBlogLayout.tsx';
import { useSingleArticle } from '@dealstack/frontend/app/pages/HelpBlog/api/useSingleArticle.ts';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader.tsx';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { ArticlePageContainer } from '@dealstack/frontend/app/pages/HelpBlog/ArticlePage/ArticlePage.styles.tsx';
import { useHelpBlogBreadcrumbs } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogBreadcrumbs.tsx';
import { Button, Flex, Text, Title } from '@mantine/core';
import {
	makeDynamicHelpBlogUrl,
	prefixHelpBlogNodeKey,
} from '@dealstack/frontend/app/pages/HelpBlog/help-blog.utils.ts';
import { SanityRichText } from '@dealstack/frontend/app/pages/HelpBlog/SanityRichText/SanityRichText.tsx';
import { PageScrollProgressBar } from '@dealstack/frontend/shared/components/PageScrollProgressBar/PageScrollProgressBar.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useClipboard } from '@mantine/hooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav';
import { HelpBlogContext } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogController.tsx';
import { HELP_BLOG_ROUTES } from '@dealstack/frontend/shared/shared-routes.ts';

export const ArticlePage = () => {
	const { dataset, perspective, mediaResolver } = useContext(HelpBlogContext);
	const tHelpBlog = useTranslationHelpBlog();
	const navigate = useNavigate();
	const clipboard = useClipboard();
	const containerRef = useRef<HTMLDivElement>(null);

	const { dealId, slug } = useParams<{ dealId: string; slug: string }>();
	const { article, isFetching, error } = useSingleArticle(dataset, slug, perspective);

	const articleCopyUrl = `${window.location.origin}${ROUTES.HELP_BLOG}/${slug}`;
	const categoryUrl = makeDynamicHelpBlogUrl(
		dealId,
		`${ROUTES.HELP_BLOG}/${generatePath(HELP_BLOG_ROUTES.CATEGORY, {
			slug: article?.category?.slug?.current || '',
		})}`
	);

	useHelpBlogBreadcrumbs([
		{
			name: tHelpBlog('breadcrumbs.article.help'),
			url: ROUTES.HELP_BLOG,
		},
		{
			name: article?.category?.name || '',
			url: `${ROUTES.HELP_BLOG}/${generatePath(HELP_BLOG_ROUTES.CATEGORY, {
				slug: article?.category?.slug?.current || '',
			})}`,
		},
		{
			name: article?.title || '',
			url: ROUTES.HELP_BLOG,
			noLink: true,
		},
	]);

	const tocLinks = useMemo(() => {
		return article?.toc?.map((item) => {
			const scrollTo = () => {
				const root = containerRef.current;
				const elementId = `#${prefixHelpBlogNodeKey(item.key)}`;

				if (root) {
					const target = root.querySelector(elementId);
					target?.scrollIntoView({ behavior: 'smooth' });
				}
			};

			return (
				<div key={item.key} className="toc-item" onClick={scrollTo}>
					{item.text}
				</div>
			);
		});
	}, [article?.toc]);

	if (isFetching) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (error || !article) {
		navigate(ROUTES.PAGE_NOT_FOUND);
		return null;
	}

	const imageUrl = article?.image
		? mediaResolver(article.image).width(2000).height(200).auto('format').url()
		: undefined;
	const formattedDate = formatStringDateToUI(
		article.createdAt,
		DATE_FORMATS.DATE_FULL_MONTH_YEAR
	);
	const readTime = article.estimatedReadingTimeMinutes || 1;
	const authorUrl = makeDynamicHelpBlogUrl(
		dealId,
		`${ROUTES.HELP_BLOG}/${generatePath(HELP_BLOG_ROUTES.AUTHOR, {
			slug: article.author.slug.current,
		})}`
	);
	const authorImageUrl = article.author?.image
		? mediaResolver(article.author.image).width(60).height(60).url()
		: null;

	const handleCopyArticleUrl = () => {
		clipboard.copy(articleCopyUrl);
		showToastV2({ message: tHelpBlog('article.copy.success') });
	};

	return (
		<HelpBlogLayout title={article.title} bannerImageUrl={imageUrl}>
			<ArticlePageContainer ref={containerRef}>
				<div className="progress-bar">
					<PageScrollProgressBar />
				</div>

				<div className="container">
					<div className="breadcrumbs">
						<QuickSwitchBreadcrumbsNav showQuickSwitch={false} />
					</div>

					<Text fz="lg" fw={600} c="dark.0" className="subtitle">
						<Link to={categoryUrl}>{article.category.name}</Link>
					</Text>

					<Title size="h1" fw={600} mb="0.5rem">
						{article.title}
					</Title>

					<Text fz="sm" className="meta">
						{formattedDate} • {readTime} {tHelpBlog('article.readTime')}
					</Text>

					<Link to={authorUrl} className="author">
						<Flex gap="0.5rem">
							{authorImageUrl && (
								<div className="author-image">
									<img src={authorImageUrl} alt={article.author.name} />
								</div>
							)}

							<div className="author-content">
								<Text className="author-name">{article.author.name}</Text>

								{article.author?.position && (
									<Text className="author-pos">{article.author.position}</Text>
								)}
							</div>
						</Flex>
					</Link>

					<Flex gap="1rem" className="layout">
						<div className="content">
							<div className="rich-text">
								<SanityRichText
									value={article.body}
									mediaResolver={mediaResolver}
									dealId={dealId}
								/>
							</div>
						</div>

						<div className="sidebar">
							<div className="sidebar-content">
								{!!tocLinks?.length && (
									<div className="toc-container">
										<Text variant="sm" fw={600} className="sidebar-title">
											{tHelpBlog('article.sidebar_title')}
										</Text>

										<Flex direction="column" gap="0.5rem" className="toc">
											{tocLinks?.map((Link) => Link)}
										</Flex>
									</div>
								)}

								<Text variant="sm" fw={600} className="copy-title">
									{tHelpBlog('article.sidebar.copy.title')}
								</Text>

								<Button
									variant="default"
									size="md"
									leftSection={<Icon variant="copy" size="xl" />}
									onClick={handleCopyArticleUrl}
								>
									{tHelpBlog('article.sidebar.copy_text')}
								</Button>
							</div>
						</div>
					</Flex>
				</div>
			</ArticlePageContainer>
		</HelpBlogLayout>
	);
};
